import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Stepper, Switch, Tag } from '../ds';
import { Sheet, SheetRow } from './Sheet';
import { DiaperToggles } from './DiaperToggles';
import { DateField, TimeField } from './TimeField';
import { Eyebrow, Mono } from './primitives';
import { useToast } from './Toast';
import * as api from '../lib/db';
import {
  ENTRY_TYPES,
  FEED_SOURCES,
  feedDefaultAmount,
  feedQtyLabel,
  feedStep,
  feedUnit,
  entryToastMessage,
} from '../lib/labels';
import { formatDurationJa, formatTime, minutesBetween } from '../lib/time';
import { resolveSleepEnd } from '../lib/entries';
import type { Child, Entry, EntryType, FeedSource, Member, SleepEntry } from '../lib/types';

/**
 * Add-entry sheet.
 *
 * Each log type gets its own fields rather than one generic amount box — the
 * shape the design landed on after the first round of feedback:
 *
 *   授乳   which breast, or ミルク / 搾乳; the amount field becomes minutes for
 *          a breast and millilitres for a bottle
 *   おむつ  おしっこ and うんち as independent toggles (both, or neither)
 *   ねんね  a start and an end, with "おわりはあとで" on by default so you can
 *          save just the start and fill the end in when they wake
 *   離乳食  time and grams
 */

export function AddSheet({
  open,
  onClose,
  familyId,
  me,
  babies,
  openSleeps,
  initialType = 'feed',
}: {
  open: boolean;
  onClose: () => void;
  familyId: string;
  me: Member;
  /** Children the baby log applies to. */
  babies: Child[];
  openSleeps: Entry[];
  initialType?: EntryType;
}) {
  const toast = useToast();

  const [type, setType] = useState<EntryType>(initialType);
  const [childId, setChildId] = useState<string>(babies[0]?.id ?? '');
  const [at, setAt] = useState<Date>(() => new Date());
  const [source, setSource] = useState<FeedSource>('right');
  const [amount, setAmount] = useState<number>(() => feedDefaultAmount('right'));
  const [grams, setGrams] = useState(30);
  const [pee, setPee] = useState(true);
  const [poop, setPoop] = useState(false);
  const [sleepStart, setSleepStart] = useState<Date>(() => new Date());
  const [sleepEnd, setSleepEnd] = useState<Date>(() => new Date());
  const [endLater, setEndLater] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset to a clean sheet each time it opens, with the clock set to now.
  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setType(initialType);
    setChildId(babies[0]?.id ?? '');
    setAt(now);
    setSource('right');
    setAmount(feedDefaultAmount('right'));
    setGrams(30);
    setPee(true);
    setPoop(false);
    setSleepStart(now);
    setSleepEnd(now);
    setEndLater(true);
    setError(null);
  }, [open, initialType, babies]);

  /** An unfinished nap for this child, if there is one. */
  const runningSleep = useMemo(
    () =>
      (openSleeps.filter((e): e is SleepEntry => e.type === 'sleep') ?? []).find(
        (e) => e.childId === childId,
      ) ?? null,
    [openSleeps, childId],
  );

  const pickSource = (next: FeedSource) => {
    setSource(next);
    setAmount(feedDefaultAmount(next));
  };

  /**
   * The end's day is explicit via the date field; the same-day midnight
   * convenience (23:40→06:20) lives in resolveSleepEnd. A result that is
   * still not after the start is invalid — surfaced as a warning, and save
   * stays disabled, rather than storing a negative-duration sleep.
   */
  const resolvedSleepEnd = useMemo(() => {
    if (endLater) return null;
    return resolveSleepEnd(sleepStart, sleepEnd);
  }, [endLater, sleepEnd, sleepStart]);
  const sleepEndInvalid = type === 'sleep' && !endLater && resolvedSleepEnd === null;

  const save = async () => {
    if (!childId) {
      setError('赤ちゃんを選んでください。');
      return;
    }
    setSaving(true);
    setError(null);

    const input: api.NewEntry =
      type === 'feed'
        ? { type: 'feed', childId, at, source, amount }
        : type === 'diaper'
          ? { type: 'diaper', childId, at, pee, poop }
          : type === 'sleep'
            ? { type: 'sleep', childId, at: sleepStart, endAt: resolvedSleepEnd }
            : { type: 'solid', childId, at, grams };

    try {
      await api.addEntry(familyId, me, input);
      toast.showSaved(entryToastMessage(input));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存できませんでした。');
    } finally {
      setSaving(false);
    }
  };

  const closeRunningSleep = async () => {
    if (!runningSleep) return;
    setSaving(true);
    try {
      await api.closeSleep(familyId, runningSleep.id, new Date(), me);
      toast.showSaved(`${formatTime(runningSleep.at)}〜${formatTime(new Date())}`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存できませんでした。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Add entry"
      title="記録を追加"
      footer={
        <>
          {error ? (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--danger)',
                marginBottom: 8,
                lineHeight: 'var(--leading-cjk)',
              }}
            >
              {error}
            </div>
          ) : null}
          <Button
            variant="primary"
            fullWidth
            iconStart="Check"
            size="lg"
            disabled={saving || sleepEndInvalid}
            onClick={save}
          >
            {saving ? '保存中…' : '保存する · Save'}
          </Button>
        </>
      }
    >
      {/* ---- type ---- */}
      <Eyebrow style={{ marginBottom: 8 }}>種類 · Type</Eyebrow>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
        {ENTRY_TYPES.map((t) => (
          <Tag key={t.key} selected={type === t.key} onClick={() => setType(t.key)}>
            {t.label}
          </Tag>
        ))}
      </div>

      {/* ---- which child, only when there is a choice ---- */}
      {babies.length > 1 ? (
        <>
          <Eyebrow style={{ marginBottom: 8 }}>だれの記録 · Child</Eyebrow>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
            {babies.map((c) => (
              <Tag key={c.id} selected={childId === c.id} onClick={() => setChildId(c.id)}>
                {c.name}
              </Tag>
            ))}
          </div>
        </>
      ) : null}

      {/* ---- 授乳 ---- */}
      {type === 'feed' ? (
        <>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <Eyebrow style={{ marginBottom: 9 }}>授乳の種類 · Source</Eyebrow>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {FEED_SOURCES.map((f) => (
                <Tag key={f.key} selected={source === f.key} onClick={() => pickSource(f.key)}>
                  {f.label}
                </Tag>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DateField value={at} onChange={setAt} />
                <TimeField value={at} onChange={setAt} />
              </div>
            </SheetRow>
            <SheetRow label={<Eyebrow>{feedQtyLabel(source)}</Eyebrow>}>
              <Stepper
                value={amount}
                onChange={setAmount}
                step={feedStep(source)}
                unit={feedUnit(source)}
                min={0}
                aria-label={feedQtyLabel(source)}
              />
            </SheetRow>
          </div>
        </>
      ) : null}

      {/* ---- おむつ ---- */}
      {type === 'diaper' ? (
        <>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <Eyebrow style={{ marginBottom: 9 }}>内容 · Contents</Eyebrow>
            <DiaperToggles
              pee={pee}
              poop={poop}
              onChange={(next) => {
                setPee(next.pee);
                setPoop(next.poop);
              }}
            />
          </div>
          <div style={{ marginTop: 14 }}>
            <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DateField value={at} onChange={setAt} />
                <TimeField value={at} onChange={setAt} />
              </div>
            </SheetRow>
          </div>
        </>
      ) : null}

      {/* ---- ねんね ---- */}
      {type === 'sleep' ? (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 4 }}>
          {/* An unfinished nap is the common case here — offer to close it
              first rather than making them start a second one. */}
          {runningSleep ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '12px 13px',
                margin: '10px 0 4px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--brand-subtle)',
              }}
            >
              <span style={{ color: 'var(--brand)', flex: 'none' }}>
                <Icon name="Moon" size={18} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>ねんね記録中</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  <Mono>{formatTime(runningSleep.at)}</Mono> から ·{' '}
                  {formatDurationJa(minutesBetween(runningSleep.at, new Date()))}
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={closeRunningSleep} disabled={saving}>
                おわりを記録
              </Button>
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>はじまり</div>
              <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Start · 眠り始めた時間</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={sleepStart} onChange={setSleepStart} />
              <TimeField value={sleepStart} onChange={setSleepStart} />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 'var(--text-sm)',
                  color: endLater ? 'var(--text-subtle)' : 'var(--text)',
                }}
              >
                おわり
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>End · あとで足せます</div>
            </div>
            {endLater ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-md)',
                  color: 'var(--text-subtle)',
                }}
              >
                — あとで
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DateField value={sleepEnd} onChange={setSleepEnd} />
                <TimeField value={sleepEnd} onChange={setSleepEnd} showNowButton />
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              padding: '14px 0 6px',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>おわりはあとで</div>
              <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Add the end when they wake</div>
            </div>
            <Switch checked={endLater} onChange={setEndLater} aria-label="おわりはあとで" />
          </div>

          {!endLater ? (
            <div
              className={resolvedSleepEnd ? undefined : 'cjk'}
              style={{
                fontSize: 'var(--text-xs)',
                color: resolvedSleepEnd ? 'var(--text-muted)' : 'var(--danger)',
                textAlign: 'right',
                paddingBottom: 8,
              }}
            >
              {resolvedSleepEnd
                ? formatDurationJa(minutesBetween(sleepStart, resolvedSleepEnd))
                : 'おわりがはじまりより前です'}
            </div>
          ) : null}

          <div
            className="cjk"
            style={{
              display: 'flex',
              gap: 9,
              alignItems: 'flex-start',
              padding: '12px 13px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface-sunken)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              marginBottom: 4,
            }}
          >
            <span style={{ color: 'var(--accent)', flex: 'none', marginTop: 1 }}>
              <Icon name="Info" size={15} />
            </span>
            一緒に寝落ちしても大丈夫。はじまりだけ保存して、起きたら「おわり」を追加できます。
          </div>
        </div>
      ) : null}

      {/* ---- 離乳食 ---- */}
      {type === 'solid' ? (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)' }}>
          <SheetRow label={<Eyebrow>日時 · Time</Eyebrow>} first>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DateField value={at} onChange={setAt} />
              <TimeField value={at} onChange={setAt} />
            </div>
          </SheetRow>
          <SheetRow label={<Eyebrow>量 · Amount</Eyebrow>}>
            <Stepper value={grams} onChange={setGrams} step={10} unit="g" min={0} aria-label="量" />
          </SheetRow>
        </div>
      ) : null}
    </Sheet>
  );
}
